import { defineStore, type Store } from "pinia";
import joi from "joi";
import {
  fromUnixToAPIDate,
  getEndDate,
  getStartDate,
  toLongDateRange,
  minutesToTime,
  toUnix,
  toExternalDateRange,
} from "@/stories/utils/dates";
import { useAuthStore } from "@/stores/auth.module";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IFileState,
  IOption,
  IVideoState,
} from "@/interfaces";
import type {
  StoreName,
  TabErrorSchemaTypes,
  RruleOptions,
  EditRepeatTypes,
  EditorModel,
  IdType,
  RRuleSupportedEntityType,
  ListResourceType,
  VisibleStateTypes,
  DefaultFilterType,
  ParticipantData,
} from "@/types";
import helpers from "@/helpers";
import {
  type NotificationSettingsInput,
  type ActivityInput,
  type SummarizedActivitiesFragment,
  Group_Connection,
  Module_Type,
  type ActivityFragment,
  type CompactInstitutionFragment,
  type EntityPagerFilter,
  Sort_Field,
  Sort_Order,
} from "@/graphql/generated-types";
import { fixValidationFields } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  IS_IBG_MANAGER,
  REPEAT_RULE_OPTIONS,
} from "@/consts";
import toImageList from "@/helpers/files/toImageList";
import toRRule from "@/helpers/rrules/toRRule";
import { RRule, RRuleSet, rrulestr } from "rrule";
import fromRRuletoRuleState from "@/helpers/rrules/fromRRuletoRuleState";
import PagerManager from "@/helpers/manager/PagerManager";
import api from "@/api";
import { useDepartments } from "@/composables/useDepartments";
import converters from "@/converters";
import { useExternalDepartments } from "@/composables/useExternalDepartments";
import { useGroups } from "@/composables/useGroups";
import { useExternalGroups } from "@/composables/useExternalGroups";
import whenComposablesReady from "@/helpers/requests/whenComposablesReady";
import getMaxDescriptionLength from "@/helpers/environment/getMaxDescriptionLength";
import { useEntityVisibility } from "@/composables/useEntityVisibility";
import ChangeTracker from "@/helpers/forms/ChangeTracker";
import { sentryException } from "@/sentry";
import { useDefaults } from "@/composables/useDefaults";
import toVideoList from "@/helpers/files/toVideoList";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  description: joi.string().max(getMaxDescriptionLength()).required(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  image: joi.array().min(1).max(30),
  pdf: joi
    .array()
    .optional()
    .items(
      joi.object({
        name: joi.string().required().max(50).min(1),
      }),
    ),
  maxSignups: joi.number().max(999999),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "description", "endDate"],
  pictures: ["image"],
  pdf: ["pdf.*.name"],
};

type VisibilityTypes = "now" | "beforeXMinutes" | "onStartTime";

export interface IActivitiesItem {
  id?: number;
  title: string;
  description: EditorModel;
  location: string;
  startDate: number;
  endDate: number;
  image?: string | null | undefined;
  imageList: IFileState[];
  pictograms: string[];
  pdf?: string | null | undefined;
  pdfToUpload: IFileState[];
  showFrom: null | number | 0; // null = visible from now | number = minutes before start date | 0 = from start time
  visibilityType: VisibilityTypes;
  visibleFromDays: number;
  visibleFromHours: number;
  visibleFromMinutes: number;
  signup: boolean;
  signupClose: number;
  signupCloseBefore: boolean;
  signupCloseDays: number;
  signupCloseHours: number;
  signupCloseMinutes: number;
  signupEnd: number;
  maxSignups: number;
  notifyNokOnSignup: boolean;
  institution?: CompactInstitutionFragment;
  videos: IVideoState[];
  price?: string;
  vacation?: boolean;
  participants: ParticipantData;
  editRepeat: EditRepeatTypes;
  rRule?: RRule | RRuleSet;
  repeatId?: string;
  category?: string;
  departments: string[];
  externalDepartments: string[];
  groups: string[];
  externalGroups: string[];
  parentGroups: string[];
  isCancelled: boolean;
  participantCount: number;
  __typename: RRuleSupportedEntityType;
  externalContentVisibility?: VisibleStateTypes;
}

export interface ActivityListFilters {
  categories: string[];
  groups: string[];
  departments: string[];
}

export interface IActivitiesState extends IDefaultState {
  listFilters: ActivityListFilters;
  item: IActivitiesItem;
  notification: NotificationSettingsInput;
  pager: PagerManager<SummarizedActivitiesFragment>;
  selectedRruleOption: IOption;
  rruleToSave?: RruleOptions;
  listResourceType: ListResourceType;
  participantTracker?: ChangeTracker;
}

export interface IActivitiesActions extends IDefaultStoreActions {
  setItemVisibility(visibility: VisibilityTypes): void;
}

export type ActivitiesStore = Store<
  string,
  IActivitiesState,
  IDefultStoreGetters<IActivitiesState>,
  IActivitiesActions
>;

const DEFUALT_ACTIVITY: IActivitiesItem = {
  __typename: "SingleActivity",
  title: "",
  description: helpers.quill.getEmptyEditorModel(),
  location: "",
  startDate: getStartDate(),
  endDate: getEndDate(),
  image: undefined,
  pdf: undefined,
  visibilityType: "now",
  visibleFromDays: 0,
  visibleFromHours: 0,
  visibleFromMinutes: 0,
  signupCloseDays: 0,
  signupCloseHours: 0,
  signupCloseMinutes: 0,
  showFrom: null,
  signup: false,
  signupClose: 0,
  signupCloseBefore: false,
  signupEnd: toUnix(new Date()),
  maxSignups: 0,
  notifyNokOnSignup: false,
  videos: [],
  imageList: [],
  pictograms: [],
  pdfToUpload: [],
  participants: {
    employeesResponsible: [],
    employeesSignedUp: [],
    residentsResponsible: [],
    residentsSignedUp: [],
  },
  editRepeat: "single",
  category: undefined,
  departments: [],
  externalDepartments: [],
  groups: [],
  externalGroups: [],
  parentGroups: [],
  isCancelled: false,
  participantCount: 0,
  externalContentVisibility: undefined,
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IActivitiesState => ({
      listFilters: {
        categories: [],
        groups: [],
        departments: [],
      },
      tabErrors: {},
      originalHash: null,
      validation: null,
      pager: new PagerManager<SummarizedActivitiesFragment>(),
      item: {
        ...DEFUALT_ACTIVITY,
      },
      notification: {
        ...DEFAULT_NOTIFICATION_SETTINGS,
      },
      selectedRruleOption: REPEAT_RULE_OPTIONS[0]!,
      rruleToSave: undefined,
      listResourceType: "internal",
    }),
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          imageList: this.item.imageList,
          pdfToUpload: this.item.pdfToUpload,
          notification: this.notification,
          selectedRruleOption: this.selectedRruleOption,
          rruleToSave: this.rruleToSave,
          externalContentVisibility: this.item.externalContentVisibility,
          videos: this.item.videos,
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        let data: ActivityFragment | undefined;

        if (this.listResourceType === "internal") {
          data = await api.activities.getActivityDetail(id);
        } else {
          data = await api.activities.getExternalActivityDetail(id);
        }

        if (!data) {
          return;
        }

        const authStore = useAuthStore();
        this.item = {
          ...DEFUALT_ACTIVITY,
          id: parseInt(data.id),
          title: data.title,
          price: data?.price?.[0]?.price
            ? (data.price[0].price / 100).toString()
            : undefined,
          location: data.location || "",
          description: helpers.quill.htmlToEditorModel(data.description || ""),
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          signupEnd: toUnix(new Date(data.signupEnd)),
          imageList: toImageList(data),
          videos: toVideoList(data),
          editRepeat: "single" as EditRepeatTypes,
          isCancelled: data.cancelled,
          // This is temporary. They will be set after the whenComposablesReady().
          departments: [],
          externalDepartments: [],
          groups: [],
          externalGroups: [],
          parentGroups: [],
          participants: {
            employeesResponsible: [],
            employeesSignedUp: [],
            residentsResponsible: [],
            residentsSignedUp: [],
          },
          participantCount: data.participants?.length || 0,
          pictograms: (data.pictograms || [])?.map((item) => item.id),
          __typename: data.__typename as RRuleSupportedEntityType,
          institution: data.institution,
          externalContentVisibility: undefined,
        };

        // We should wait the list to be loaded to select departments. Because API
        // returns departments and external departments in the same array. We don't
        // know which id is related with which list. That causes a race condition
        // issue.
        const departments = useDepartments();
        const externalDepartments = useExternalDepartments(
          Module_Type.Activity,
        );
        const groups = useGroups(Module_Type.Activity);
        const externalGroups = useExternalGroups(Group_Connection.Activity);
        let entityVisibility = undefined;

        if (this.listResourceType === "external") {
          entityVisibility = useEntityVisibility(Module_Type.Activity);
          // No need to await here. whenComposablesReady() function will handle it
          entityVisibility.load();
        }

        // We don't know when the composable data will be loaded. But we can not
        // wait to select an item because it effects lots of things such as
        // spinners, warnings, etc. Because of the race condition issue, setting
        // the following data should be wait a little. This will be occurred only
        // when the user refresh the page on activities. Once the data is ready,
        // users can't notice any difference.
        whenComposablesReady(
          [departments, externalDepartments, groups, externalDepartments],
          () => {
            this.item.departments = converters.shared.toSelectableIds(
              departments.departmentList,
              data.departments,
            );
            this.item.externalDepartments = converters.shared.toSelectableIds(
              externalDepartments.externalDepartmentList,
              data.departments,
            );

            this.item.groups = converters.shared.toSelectableIds(
              groups.groupList,
              data.groups,
            );
            this.item.externalGroups = converters.shared.toSelectableIds(
              externalGroups.externalGroupList,
              data.groups,
            );
            this.item.parentGroups = converters.shared.toSelectableIds(
              groups.parentGroupList,
              data.groups,
            );
            if (entityVisibility) {
              this.item.externalContentVisibility = entityVisibility.isVisible(
                this.item.id,
              );
            }

            this.setOriginalHash();
          },
        );

        // Setting the rrule data
        if (data.__typename === "RepeatingActivity" && data.repeat) {
          const rule = rrulestr(data.repeat.rrule);
          this.rruleToSave = fromRRuletoRuleState(rule);
          this.item.rRule = rule;
          this.item.repeatId = data.repeat.id;
        }

        this.item.category = data.category?.id || undefined;

        this.item.pdfToUpload = [];

        if (data.documents && data.documents.length > 0) {
          const [document] = data.documents;
          if (document?.url.includes(".pdf")) {
            this.item.pdfToUpload = [
              {
                id: document.id,
                name: document.title,
                url: document.url,
                base64: "",
                type: "pdf",
              },
            ];
          }
        }

        // Visibility options
        if (data.showFrom === null) {
          this.item.visibilityType = "now";
        } else if (data.showFrom === 0) {
          this.item.visibilityType = "onStartTime";
        } else {
          this.item.visibilityType = "beforeXMinutes";
          const { days, hours, minutes } = minutesToTime(data.showFrom || 0);
          this.item.visibleFromDays = days;
          this.item.visibleFromHours = hours;
          this.item.visibleFromMinutes = minutes;
        }

        // Signup options
        if (data.signupClose) {
          this.item.signupCloseBefore = true;
          const { days, hours, minutes } = minutesToTime(data.signupClose);
          this.item.signupCloseDays = days;
          this.item.signupCloseHours = hours;
          this.item.signupCloseMinutes = minutes;
        }

        this.item.signup = data.signupEnabled;
        this.item.maxSignups = data.maxSignups || 0;
        this.item.notifyNokOnSignup = data.notifyNokOnSignup || false;
        this.item.participants = converters.activity.toParticipantsData(data);

        const { getActivityNotificationSettings } = useDefaults();

        if (data.notificationSettings) {
          const { status, reminderFirst, reminderSecond } =
            data.notificationSettings;
          this.notification = {
            ...getActivityNotificationSettings(),
            status,
            reminderFirst,
            reminderSecond,
          };
        } else if (this.item.id) {
          this.notification = {
            ...DEFAULT_NOTIFICATION_SETTINGS,
          };
        } else {
          this.notification = {
            ...getActivityNotificationSettings(),
          };
        }

        this.notification = {
          ...this.notification,
          entityId: data.id,
          institution:
            data.institution?.id?.toString() || authStore.institutionId,
        };

        // We need to track those changes on participants
        this.participantTracker = new ChangeTracker(this.item.participants, [
          "employeesResponsible",
          "employeesSignedUp",
          "residentsResponsible",
          "residentsSignedUp",
        ]);

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): ActivityInput {
        const authStore = useAuthStore();
        const item = { ...this.item };

        const id = item?.id?.toString();

        if (item.signup && item.signupCloseBefore) {
          const daysInMinutes = item.signupCloseDays
            ? item.signupCloseDays * 1440
            : 0;
          const hoursInMinutes = item.signupCloseHours
            ? item.signupCloseHours * 60
            : 0;
          const minutes = item.signupCloseMinutes ? item.signupCloseMinutes : 0;

          item.signupClose = daysInMinutes + hoursInMinutes + minutes;
        }

        // Let's set the maxSignup value
        let maxSignup = 0;
        if (item.signup && item.maxSignups) {
          maxSignup = item.maxSignups;
        }

        if (item.visibilityType === "now") {
          item.showFrom = null;
        } else if (item.visibilityType === "onStartTime") {
          item.showFrom = 0;
        } else {
          const daysInMinutes = item.visibleFromDays
            ? item.visibleFromDays * 1440
            : 0;
          const hoursInMinutes = item.visibleFromHours
            ? item.visibleFromHours * 60
            : 0;
          const minutes = item.visibleFromMinutes ? item.visibleFromMinutes : 0;

          item.showFrom = daysInMinutes + hoursInMinutes + minutes;
        }

        let rruleString;
        let isRepeat = false;
        let repeat;
        // Setting the repeat rule for repeating activities
        if (this.rruleToSave) {
          this.rruleToSave.startDate = this.item.startDate;
          const rule = toRRule(this.rruleToSave);
          rruleString = rule.toString();
          isRepeat = true;
          repeat = item.repeatId;
        }

        // We shouldn't marked as repeat on updates if it's not selected a repeat
        if (id && this.item.editRepeat === "single") {
          isRepeat = false;
          repeat = null;
          rruleString = null;
        }

        const { departments, groups } = converters.shared.toSharedOptions(item);

        const input: ActivityInput = {
          id,
          institution:
            item.institution?.id?.toString() || authStore.institutionId,
          title: item.title,
          isRepeat,
          rrule: rruleString,
          repeat,
          signup: item.signup,
          notifyNokOnSignup: item.notifyNokOnSignup,
          status: item.isCancelled ? 2 : 1,
          image: item.image,
          pictograms: item.pictograms,
          pdf: item.pdf,
          startDate: fromUnixToAPIDate(item.startDate),
          endDate: fromUnixToAPIDate(item.endDate),
          showFrom: item.showFrom,
          signupClose: item.signupCloseBefore ? item.signupClose : null,
          maxSignup,
          location: item.location,
          // We have to send NULL to make the field empty. Undefined doesn't work
          category: item.category || null,
          description: helpers.quill.editorModelToHtml(item.description),
          ...(item.price && { price: parseFloat(item.price) }),
          departments,
          groups,
        };

        // We should only save those values if they're changed
        this.participantTracker?.appendIfChanged(input, "employeesResponsible");
        this.participantTracker?.appendIfChanged(input, "employeesSignedUp");
        this.participantTracker?.appendIfChanged(input, "residentsResponsible");
        this.participantTracker?.appendIfChanged(input, "residentsSignedUp");

        return input;
      },
      async hasStaleData() {
        // If there is no change, we shouldn't check stale data for participants
        if (this.participantTracker?.hasAnyChange() !== true) {
          return false;
        }

        const freshData = await api.activities.getActivityDetail(this.item.id);
        if (!freshData) {
          sentryException(new Error("The selected item not found"));
          return;
        }

        // Let's check if the initial version were different than the fresh version
        return this.participantTracker?.isStale(
          converters.activity.toParticipantsData(freshData),
        );
      },
      setItemVisibility(visibility: VisibilityTypes) {
        this.item.visibilityType = visibility;
      },
      async isNotValid() {
        this.validation = helpers.forms.validate(VALIDATION_SCHEMA, {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.description),
          image: this.item.imageList.filter((item) => item.action !== "delete"),
          pdf: IS_IBG_MANAGER
            ? this.item.pdfToUpload.filter((item) => item.action !== "delete")
            : [],
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.start"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        if (filter === "show_external_content") {
          const { dateFrom, dateTo } = toExternalDateRange();
          this.listResourceType = "external";

          const params: EntityPagerFilter = {
            dateFrom,
            dateTo,
            sorting: {
              field: Sort_Field.StartDate,
              order: Sort_Order.Asc,
            },
          };
          await this.pager.paginate(
            api.activities.getExtarnalActivities,
            params,
            this,
          );
        } else {
          this.listResourceType = "internal";
          const { dateFrom, dateTo } = toLongDateRange(
            filter === "show_upcoming",
          );

          const params: EntityPagerFilter = {
            dateFrom,
            dateTo,
            sorting: {
              field: Sort_Field.StartDate,
              order:
                filter === "show_upcoming" ? Sort_Order.Asc : Sort_Order.Desc,
            },
          };
          await this.pager.paginate(api.activities.getActivities, params, this);
        }
      },
    },
  });
};

// Activities View store
export const useActivitiesStore = createUseStore("ACTIVITIES");

// We use the same store structure for the create modal.
export const useAddActivitiessStore = createUseStore("ADD_ACTIVITIES");
